import { AddressState } from '@evorait/pace-core';

/** Fields left out of the search text because matching against them would only confuse the user. */
const EXCLUDED_SEARCH_FIELDS: ReadonlySet<string> = new Set(['id', 'addrKey', 'addrNumber']);

/**
 * Strips case and diacritics so `furth` finds `Fürth` regardless of how either side was typed.
 *
 * This covers accents built from a base letter plus a combining mark (the `NFD` decomposition
 * splits them apart, and the combining-mark range is then dropped), which is most of Latin script.
 * It does not fold letters that are not accent-plus-base, such as German `ß` — matching `ß` still
 * needs `ß` or `ss` typed as such.
 *
 * @param value the text to normalize
 * @returns the lowercase, diacritic-stripped text
 */
function normalize(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Every string field of an address, normalized, excluding the bookkeeping fields that would
 * otherwise produce nonsense matches: `id` and `addrKey` are opaque identifiers, and a local
 * address's `addrNumber` is `manual-<uuid>`, so leaving it in would let a query match random hex.
 *
 * Derived from the object's own entries rather than a hand-written field list, so a field added to
 * `AddressState` later is searchable without anyone remembering to update this — booleans (such as
 * `visible` or `isFromAddressSet`) are skipped by construction, since `typeof` filters to strings.
 *
 * @param address the address to index
 * @param extraSearchText further text to index as fields of their own, such as the full names behind
 * the `country` and `stateOfUSA` codes the address stores
 * @returns the normalized text of each searchable field, kept separate so a match can be required
 * to fall within one field rather than across all of them
 */
function searchableFields(address: AddressState, extraSearchText: string[]): string[] {
    return [
        ...Object.entries(address)
            .filter(([key, value]) => typeof value === 'string' && !EXCLUDED_SEARCH_FIELDS.has(key))
            .map(([, value]) => value as string),
        ...extraSearchText,
    ].map(normalize);
}

/**
 * The full country name for an ISO 3166 alpha-2 code, in the given language — the name the user
 * reads, where the address only stores the code.
 *
 * @param countryCode the code stored on the address, e.g. `DE`
 * @param locale the language to name the country in
 * @returns the name, or `undefined` when the code is not a well-formed two-letter code (which
 * `Intl.DisplayNames` would throw on) or the runtime does not know it
 */
export function countryName(countryCode: string | undefined, locale: string): string | undefined {
    if (!countryCode || !/^[a-z]{2}$/i.test(countryCode)) {
        return undefined;
    }
    try {
        return new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' }).of(countryCode.toUpperCase());
    } catch {
        // an unsupported locale tag throws as well; the code itself is still searchable
        return undefined;
    }
}

/**
 * Whether `address` matches the free-text search `query`.
 *
 * The query is split on whitespace into tokens, and every token must match somewhere (AND
 * semantics), so a two-word query such as "erlangen henke" finds the address regardless of which
 * field each word sits in. Each token, in turn, has to appear as a plain substring of one field:
 * contiguous, and within that single field rather than across the concatenation of all of them.
 * Nothing looser — characters found in order with gaps between them used to match too, which let
 * a short query pull in addresses that plainly had nothing to do with it.
 *
 * Both sides are normalized (lowercased, diacritics stripped) before matching — see {@link normalize}.
 *
 * @param address the address to test
 * @param query the raw search text as typed by the user
 * @param extraSearchText additional searchable fields derived from the address, which the address
 * itself cannot carry — see {@link searchableFields}
 * @returns whether every token in `query` matches some field of `address`; an empty or
 * whitespace-only query always matches
 */
export function matchesAddressSearch(address: AddressState, query: string, extraSearchText: string[] = []): boolean {
    const tokens = normalize(query).trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
        return true;
    }

    const fields = searchableFields(address, extraSearchText);
    return tokens.every((token) => fields.some((field) => field.includes(token)));
}
